/**
 * Area Service
 *
 * The CMS-managed vocabulary of physical areas ("Lobby", "East Stairwell",
 * "Loading Dock") that an inspection photo is tagged with.
 *
 * Areas replaced a free-text field. Free text was fine for capture but useless
 * for reporting — "Mens Restroom", "Men's restroom" and "MENS RR" are three
 * different areas to any grouping — so Super Admins curate the list in the CMS
 * and the field UI picks from it.
 *
 * One shared list for every customer — areas were per company until Oct 2026
 * (beta feedback: "It just needs to be generic areas we can add to any
 * inspection, with any client.").
 *
 * Backend endpoints:
 *   GET    /api/v1/areas          — list (?active=1 for the picker)
 *   POST   /api/v1/areas          — create (admin/super admin/developer)
 *   PATCH  /api/v1/areas/:id      — rename, reorder, or deactivate
 *   DELETE /api/v1/areas/:id      — remove (photos keep their row, lose the tag)
 */

import { api } from './api';

/* ============================================
 * TYPES
 * ============================================ */

export interface Area {
	id: number;
	name: string;
	/** Retired areas stay on historical photos but leave the picker. */
	isActive: boolean;
	/** Manual ordering in the picker; ties break on name. */
	sortOrder: number;
	createdBy: number | null;
}

/** Raw snake_case row as returned by the API. */
interface ApiArea {
	id: number;
	name: string;
	is_active: boolean;
	sort_order: number;
	created_by: number | null;
}

export interface AreaFilters {
	/** '1' to return only active areas — what the field picker wants. */
	active?: string;
	search?: string;
}

export interface CreateAreaData {
	name: string;
	is_active?: boolean;
	sort_order?: number;
}

export interface UpdateAreaData {
	name?: string;
	is_active?: boolean;
	sort_order?: number;
}

/* ============================================
 * MAPPING
 * ============================================ */

/** Map an API row to the camelCase shape the app uses. */
const toArea = (row: ApiArea): Area => ({
	id: row.id,
	name: row.name,
	isActive: Boolean(row.is_active),
	sortOrder: Number(row.sort_order ?? 0),
	createdBy: row.created_by ?? null,
});

/* ============================================
 * SERVICE
 * ============================================ */

export const areaService = {
	/** List areas, optionally active-only. */
	list: async (filters?: AreaFilters): Promise<Area[]> => {
		const res = await api.get<{ data: ApiArea[] }>('/areas', filters as Record<string, string> | undefined);
		const rows = (res.data as any)?.data ?? [];
		return (rows as ApiArea[]).map(toArea);
	},

	/**
	 * Active areas — the exact query the field photo picker makes. Returns an
	 * empty list rather than throwing when nothing is configured yet, so the
	 * picker can fall back to free text.
	 */
	listActive: async (): Promise<Area[]> => {
		try {
			return await areaService.list({ active: '1' });
		} catch {
			return [];
		}
	},

	create: async (data: CreateAreaData): Promise<Area> => {
		const res = await api.post<{ data: ApiArea }>('/areas', data);
		return toArea((res.data as any).data);
	},

	update: async (id: number | string, data: UpdateAreaData): Promise<Area> => {
		const res = await api.patch<{ data: ApiArea }>(`/areas/${id}`, data);
		return toArea((res.data as any).data);
	},

	remove: (id: number | string) => api.delete<void>(`/areas/${id}`),
};

export default areaService;
